import { useEffect, useRef, useState } from 'react'
import { MenuDeFundo, useNivelDoMicrofone } from './controles-de-midia.tsx'
import { SeletorDeAparelho } from './SeletorDeAparelho.tsx'
import { MenuDeAparencia } from './MenuDeAparencia.tsx'
import { aparenciaEstaNeutra, type Aparencia } from './aparencia.ts'
import { VideoDoConvidado } from './ConvidadoNaSala.tsx'
import { Portaria, type PedidoNaPorta } from './Portaria.tsx'
import type { ParticipanteAoVivo } from './sala-mesh.ts'
import type { EstiloDeFundo } from './fundo.ts'

/**
 * ANTESSALA da chamada (SPEC-20260917-0047).
 *
 * O botão de chamar não disca mais na hora: ele abre esta tela, onde a pessoa
 * se vê, ouve o próprio microfone reagir, escolhe os aparelhos e o fundo — e só
 * então aperta "Chamar agora". Nada sai para o outro lado enquanto isto está
 * aberto; o telefone de ninguém toca.
 *
 * O desenho segue a sala de espera do Meet, que o usuário mostrou como
 * referência: a própria imagem grande no meio e, ao lado, quem vai ser chamado
 * com o botão de discar.
 */
export function Antessala({
  outro,
  video,
  local,
  faixaDoMicrofone,
  micLigado,
  camLigada,
  estiloFundo,
  imagemFundo,
  aparencia,
  onMic,
  onCam,
  onTrocarMicrofone,
  onTrocarCamera,
  onTrocarFundo,
  onTrocarAparencia,
  onChamar,
  onCancelar,
  linkDoConvite,
  naPorta,
  convidados,
  onDecidir,
}: {
  outro: { memberId: string; nome: string; foto: string | null }
  video: boolean
  local: MediaStream | null
  faixaDoMicrofone: MediaStreamTrack | null
  micLigado: boolean
  camLigada: boolean
  estiloFundo: EstiloDeFundo
  imagemFundo: string | null
  aparencia: Aparencia
  onMic: () => void
  onCam: () => void
  onTrocarMicrofone: (id: string) => void
  onTrocarCamera: (id: string) => void
  onTrocarFundo: (estilo: EstiloDeFundo, imagem?: string | null) => void
  onTrocarAparencia: (nova: Aparencia) => void
  onChamar: () => void
  onCancelar: () => void
  /** URL para quem é de fora entrar; null enquanto a sala não nasceu. */
  linkDoConvite: string | null
  /** Quem bateu na porta enquanto eu me preparava. */
  naPorta: PedidoNaPorta[]
  /** Quem já entrou pelo link (dá para conversar antes mesmo de discar). */
  convidados: ParticipanteAoVivo[]
  onDecidir: (guestId: string, aprovar: boolean) => void
}) {
  const previa = useRef<HTMLVideoElement>(null)
  const [menuAparencia, setMenuAparencia] = useState<HTMLElement | null>(null)
  const [menuFundo, setMenuFundo] = useState<HTMLElement | null>(null)
  const [copiado, setCopiado] = useState(false)
  const nivel = useNivelDoMicrofone(faixaDoMicrofone, micLigado)

  useEffect(() => {
    if (previa.current) previa.current.srcObject = local
  }, [local])

  // Esc desiste da chamada antes de ela existir.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [onCancelar])

  return (
    <div
      className={`vc-antessala${menuAparencia ? ' vc-antessala--com-aparencia' : ''}`}
      role="dialog"
      aria-label="Preparar a chamada"
    >
      {/* Palco e controles numa coluna: os botões ficam ABAIXO do vídeo, não
          sobre ele (pedido do usuário, 2026-09-17 23:43, com o Meet ao lado). */}
      <div className="vc-antessala__coluna">
      <div className="vc-antessala__palco">
        {/* `muted` é obrigatório: sem isso a própria voz volta pela caixa. */}
        <video
          ref={previa}
          className={`vc-antessala__video${camLigada ? '' : ' vc-antessala__video--off'}`}
          autoPlay
          playsInline
          muted
        />
        {camLigada ? null : (
          <div className="vc-antessala__semcam">
            <span className="vc-antessala__avatar" aria-hidden>
              <i className="fas fa-video-slash" />
            </span>
            <p>Câmera desligada</p>
          </div>
        )}

        {/* Quem já entrou pelo link aparece aqui — dá para conversar com
            alguém de fora antes mesmo de chamar quem é da conversa. */}
        {convidados.length > 0 ? (
          <div className="vc-tela__convidados">
            {convidados.map((p) => (
              <VideoDoConvidado key={p.id} participante={p} />
            ))}
          </div>
        ) : null}

        <Portaria pedidos={naPorta} onDecidir={onDecidir} />
      </div>

      <div className="vc-antessala__controles">
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${micLigado ? '' : ' vc-botao--desligado'}`}
            aria-pressed={!micLigado}
            aria-label={micLigado ? 'Desligar o microfone' : 'Ligar o microfone'}
            title={micLigado ? 'Desligar o microfone' : 'Ligar o microfone'}
            onClick={onMic}
          >
            <i className={micLigado ? 'fas fa-microphone' : 'fas fa-microphone-slash'} aria-hidden />
            {/* Anel que pulsa com a voz: mostra que o microfone está pegando. */}
            {micLigado ? (
              <span className="vc-botao__pulso" style={{ transform: `scale(${1 + nivel * 0.5})` }} aria-hidden />
            ) : null}
          </button>
          {/* Uma seta só para o ÁUDIO: quem abre isto quer resolver o som, e
              som são as duas pontas — o que entra e o que sai. */}
          <SeletorDeAparelho
            rotulo="Escolher microfone e som"
            linhas={[
              { tipo: 'microfone', titulo: 'Microfone', icone: 'fas fa-microphone' },
              { tipo: 'saida', titulo: 'Som sai por', icone: 'fas fa-volume-up' },
            ]}
            faixaDoMicrofone={faixaDoMicrofone}
            micLigado={micLigado}
            onTrocarMicrofone={onTrocarMicrofone}
            elementoDeSaida={previa.current}
          />
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${camLigada ? '' : ' vc-botao--desligado'}`}
            aria-pressed={!camLigada}
            aria-label={camLigada ? 'Desligar a câmera' : 'Ligar a câmera'}
            title={camLigada ? 'Desligar a câmera' : 'Ligar a câmera'}
            onClick={onCam}
          >
            <i className={camLigada ? 'fas fa-video' : 'fas fa-video-slash'} aria-hidden />
          </button>
          <SeletorDeAparelho
            rotulo="Escolher a câmera"
            linhas={[{ tipo: 'camera', titulo: 'Câmera', icone: 'fas fa-video' }]}
            onTrocarCamera={onTrocarCamera}
          />
          {/* APARÊNCIA (SPEC-20260918-0040): suavizar, clarear e nitidez.
              Fica ao lado do fundo porque são a mesma família de decisão — como
              você aparece —, mas é botão próprio: dá para mexer na luz sem
              nunca abrir o fundo. */}
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${aparenciaEstaNeutra(aparencia) ? '' : ' vc-botao--ativo'}`}
            aria-haspopup="dialog"
            aria-expanded={menuAparencia !== null}
            aria-label="Aparência da câmera"
            title={camLigada ? 'Aparência' : 'Ligue a câmera para ajustar a aparência'}
            disabled={!camLigada}
            onClick={(e) => {
              const alvo = e.currentTarget
              setMenuAparencia((atual) => (atual ? null : alvo))
            }}
          >
            <i className="fas fa-wand-magic-sparkles" aria-hidden />
          </button>
          {/* o painel NÃO sai daqui: ele é coluna da antessala, ao lado do
              vídeo — ver o <aside> no fim desta tela */}
          <button
            type="button"
            className={`vc-botao vc-botao--redondo${estiloFundo !== 'nenhum' ? ' vc-botao--ativo' : ''}`}
            aria-haspopup="dialog"
            aria-expanded={menuFundo !== null}
            aria-label="Fundo da câmera"
            title={camLigada ? 'Fundo' : 'Ligue a câmera para escolher o fundo'}
            disabled={!camLigada}
            onClick={(e) => {
              const alvo = e.currentTarget
              setMenuFundo((atual) => (atual ? null : alvo))
            }}
          >
            <i className="fas fa-image" aria-hidden />
          </button>
          {menuFundo ? (
            <MenuDeFundo
              ancora={menuFundo}
              estilo={estiloFundo}
              imagem={imagemFundo}
              onFechar={() => setMenuFundo(null)}
              onTrocar={onTrocarFundo}
            />
          ) : null}
        </div>


    </div>

      {/* APARÊNCIA como COLUNA, entre o vídeo e o painel de chamar (pedido do
          usuário, 2026-09-18 23:44): mexer em luz e retoque só faz sentido
          olhando o rosto, e o painel flutuante cobria justamente ele. */}
      {menuAparencia ? (
        <MenuDeAparencia
          botao={menuAparencia}
          aparencia={aparencia}
          onFechar={() => setMenuAparencia(null)}
          onTrocar={onTrocarAparencia}
        />
      ) : null}

      <aside className="vc-antessala__painel" data-coluna>
        <h2 className="vc-antessala__titulo">Tudo pronto para chamar</h2>
        <p className="vc-antessala__sub">
          Confira sua imagem e seu som. Ninguém é avisado enquanto você está aqui.
        </p>

        <h3 className="vc-antessala__secao">Quem vai ser chamado</h3>
        <ul className="vc-antessala__pessoas">
          <li className="vc-antessala__pessoa">
            <span className="vc-antessala__foto" aria-hidden>
              {outro.foto ? <img src={outro.foto} alt="" /> : iniciais(outro.nome)}
            </span>
            <span className="vc-antessala__nome">{outro.nome}</span>
          </li>
        </ul>

        <button type="button" className="vc-antessala__chamar" onClick={onChamar}>
          <i className={video ? 'fas fa-video' : 'fas fa-phone-alt'} aria-hidden /> Chamar agora
        </button>

        {/*
          Convidar quem NÃO tem Orbind: o link leva a uma sala de espera, e
          ninguém entra sem você deixar (SPEC-20260917-0047).
        */}
        <h3 className="vc-antessala__secao">Convidar alguém de fora</h3>
        {linkDoConvite ? (
          <>
            <div className="vc-antessala__link">
              <input
                className="vc-antessala__url"
                value={linkDoConvite}
                readOnly
                aria-label="Link do convite"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="vc-antessala__copiar"
                aria-label="Copiar o link do convite"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(linkDoConvite)
                    .then(() => setCopiado(true))
                    .catch(() => setCopiado(false))
                }}
              >
                <i className={copiado ? 'fas fa-check' : 'far fa-copy'} aria-hidden />{' '}
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            <p className="vc-antessala__nota">
              Quem abrir o link espera na porta até você deixar entrar.
            </p>
          </>
        ) : (
          <p className="vc-antessala__nota">Preparando o link…</p>
        )}
        <button type="button" className="vc-antessala__cancelar" onClick={onCancelar}>
          Cancelar
        </button>
      </aside>
    </div>
  )
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}
